import React from 'react';
import styled from 'styled-components';
import glassTheme from '../../styles/glassTheme';
import type { Student } from '../../types/teacherAnalytics';
import { DateView, StudentLink } from './AnalyticsShared';

const CardList = styled.ul`
  display: grid; grid-template-columns: minmax(0, 1fr);
  gap: ${glassTheme.spacing.md}; margin: ${glassTheme.spacing.lg} 0;
  padding: 0; list-style: none;
  > li { min-width: 0; }
`;
const Card = styled.article`
  ${glassTheme.glass.light}
  overflow-wrap: anywhere;
  h3 { margin: 0; font-size: 1.25rem; line-height: 1.4; font-weight: 700; }
`;
const Disclosure = styled.details`
  && { margin: 0; }
  > summary {
    display: grid; grid-template-columns: auto minmax(140px, 1fr) minmax(0, 2fr) auto auto;
    align-items: center; gap: ${glassTheme.spacing.md};
    list-style: none; min-height: 72px; padding: ${glassTheme.spacing.md} ${glassTheme.spacing.lg};
    cursor: pointer; border-radius: ${glassTheme.borderRadius.lg};
  }
  > summary::-webkit-details-marker { display: none; }
  > summary::after { content: '+'; grid-column: 5; grid-row: 1; font-size: 1.5rem; color: ${glassTheme.colors.accent.cyan}; }
  > summary:hover { background: ${glassTheme.colors.border}; }
  &[open] > summary { border-bottom: 1px solid ${glassTheme.colors.border}; border-radius: ${glassTheme.borderRadius.lg} ${glassTheme.borderRadius.lg} 0 0; }
  &[open] > summary::after { content: '−'; }
  @media (max-width: 768px) {
    > summary { padding: ${glassTheme.spacing.md}; grid-template-columns: auto minmax(0, 1fr) auto; }
    > summary::after { grid-column: 3; }
  }
`;
const RowSummary = styled.span`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: ${glassTheme.spacing.md};
  font-size: 1rem; line-height: 1.5;
  > span { display: block; }
  .summary-label { display: block; color: ${glassTheme.colors.text.secondary}; font-size: .875rem; margin-bottom: ${glassTheme.spacing.xs}; }
  .summary-value { display: block; font-weight: 600; }
  @media (max-width: 768px) {
    grid-column: 1 / -1; grid-row: 2;
    grid-template-columns: repeat(2, minmax(0, 1fr)); gap: ${glassTheme.spacing.sm} ${glassTheme.spacing.md};
    > span:last-child:nth-child(3) {
      grid-column: 1 / -1; display: flex; flex-wrap: wrap; align-items: baseline; gap: ${glassTheme.spacing.sm};
    }
  }
`;
const Content = styled.div`
  padding: ${glassTheme.spacing.lg}; display: grid; gap: ${glassTheme.spacing.lg};
  @media (max-width: 768px) { padding: ${glassTheme.spacing.md}; }
`;
const Avatar = styled.span`
  width: ${glassTheme.spacing.xxl}; height: ${glassTheme.spacing.xxl};
  display: grid; place-items: center; border-radius: ${glassTheme.borderRadius.full};
  background: ${glassTheme.colors.accent.blue}; color: ${glassTheme.colors.background};
  font-size: 1.25rem; font-weight: 700;
`;
const MetricList = styled.dl`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${glassTheme.spacing.lg} ${glassTheme.spacing.md}; margin: 0;
  > div { border-left: 2px solid ${glassTheme.colors.border}; padding-left: ${glassTheme.spacing.sm}; }
  dt { color: ${glassTheme.colors.text.secondary}; font-size: .875rem; line-height: 1.5; margin-bottom: ${glassTheme.spacing.sm}; }
  dd { margin: 0; font-size: 1rem; line-height: 1.6; font-weight: 600; }
  small { font-size: .875rem; line-height: 1.6; font-weight: 400; }
`;
const Information = styled.dl`
  margin: 0; font-size: .875rem; line-height: 1.7;
  dt { color: ${glassTheme.colors.text.secondary}; }
  dd { margin: 0 0 ${glassTheme.spacing.sm}; }
  code { font-size: inherit; user-select: text; overflow-wrap: anywhere; }
`;
const Actions = styled.span`
  grid-column: 4; grid-row: 1;
  a { display: inline-flex; align-items: center; justify-content: center; min-height: 44px;
    padding: ${glassTheme.spacing.sm} ${glassTheme.spacing.md}; border: 1px solid ${glassTheme.colors.border};
    border-radius: ${glassTheme.borderRadius.md}; text-decoration: underline; }
  a:hover { border-color: ${glassTheme.colors.borderHover}; }
  @media (max-width: 768px) {
    grid-column: 1 / -1; grid-row: 3;
    a { width: 100%; }
  }
`;

interface Metric { label: string; value: React.ReactNode }
export function StudentCards<T extends Student>({students, courseId, topicId, label, metrics, summaryMetrics, showEnrollment = false}: {
  students: readonly T[];
  courseId: string;
  topicId?: string;
  label: string;
  metrics: (student: T) => readonly Metric[];
  summaryMetrics?: (student: T) => readonly Metric[];
  showEnrollment?: boolean;
}) {
  if (!students.length) return null;
  return <CardList aria-label={label}>{students.map(student => {
    const name = student.name || '未命名学生';
    const headingId = `student-card-${courseId}-${topicId || 'course'}-${student.studentId}`;
    const overview = [
      ...(summaryMetrics?.(student) || []),
      {label: '课程注册', value: <DateView value={student.enrolledAt} enrollment />}
    ];
    return <li key={student.studentId}><Card aria-labelledby={headingId}>
      <Disclosure>
      <summary id={`student-${student.studentId}-name`} aria-labelledby={headingId}
        aria-describedby={`${headingId}-summary`} aria-controls={`${headingId}-details`}>
        <Avatar aria-hidden="true">{Array.from(name.trim())[0] || '?'}</Avatar>
        <h3 id={headingId}>{name}</h3>
        <RowSummary id={`${headingId}-summary`}>{overview.map(metric => <span key={metric.label}>
          <span className="summary-label">{metric.label}</span>
          <span className="summary-value">{metric.value}</span>
        </span>)}</RowSummary>
        <Actions onClick={event => event.stopPropagation()}>
          <StudentLink student={student} courseId={courseId} topicId={topicId} />
        </Actions>
      </summary>
      <Content id={`${headingId}-details`} role="region" aria-labelledby={headingId}>
      <MetricList>{metrics(student).map(metric => <div key={metric.label}>
        <dt>{metric.label}</dt><dd>{metric.value}</dd>
      </div>)}</MetricList>
      <Information>
        <dt>学生标识</dt><dd><code>{student.studentId}</code></dd>
        {showEnrollment && <><dt>注册时间</dt><dd><DateView value={student.enrolledAt} enrollment /></dd></>}
      </Information>
      </Content>
      </Disclosure>
    </Card></li>;
  })}</CardList>;
}
